// i18next-extract-mark-ns-start HowItWorks

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { isZipcodeCover } from '../../../services/zipcode';
import {
  margins, colors, mobileThresholdPixels,
} from '../v4/styledComponents';
import { slashNtoBr, toFormattedBoldText } from '../../../services/formatting';
import {
  introHowItWorksData, atHomeMoreHowItWorksData, packageMoreHowItWorksData, StepDataType,
  stepsPackage,
  stepsAtHome,
} from './HowItWorksData';
import withAppContext from '../../../withAppContext';

const SubContent = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${margins.m5};
  margin-top: ${margins.m6};
  @media (max-width: ${mobileThresholdPixels}) {
    flex-direction: column;
    gap: ${margins.m3};
    margin-top: ${margins.m3};
  }
`;
const BlockContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${margins.m3};
  @media (max-width: ${mobileThresholdPixels}) {
    gap: ${margins.m1};
    width: 100%;
  }
`;
const SubtitleContainer = styled.div`
  @media (max-width: ${mobileThresholdPixels}) {
    display: flex;
    width: 100%;
    gap: ${margins.m2};
  }
`;
const Subtitle = styled.p`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 22px;
    margin-top: 6px;
  }
`;
const NumberContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: end;
  @media (max-width: ${mobileThresholdPixels}) {
    gap: 5px;
  }
`;
const Dot = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${colors.black};
  border-radius: 50%;
  margin-bottom: 17px;
  @media (max-width: ${mobileThresholdPixels}) {
    width: 8px;
    height: 8px;
    margin-bottom: 13px;
  }
`;
const Number = styled.p`
  font-size: 70px;
  font-weight: 700;
  margin: 0;
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 55px;
  }
`;
type TextProps = {
  bold?: boolean;
  uppercase?: boolean;
};
const Text = styled.p<TextProps>`
  font-size: 16px;
  margin: 0;
  line-height: 24px;
  font-weight: ${(props) => (props.bold ? 700 : 400)};
  text-transform: ${(props) => (props.uppercase ? 'uppercase' : 'none')};
`;

interface MainHowItWorksProps {
  context: {
    isMobile: boolean;
  };
  zipcode: string;
  countryId: string;
  isZipcodeComplete: boolean;
  landingPageType: string;
  position?: string;
  isPackage?: boolean;
}

const MainHowItWorks = ({
  context: { isMobile }, zipcode, countryId = 'FR', isZipcodeComplete, landingPageType, position, isPackage,
}: MainHowItWorksProps) => {
  const { t } = useTranslation();
  const data: StepDataType[] = !isZipcodeCover(zipcode, countryId) ? packageMoreHowItWorksData(t) : atHomeMoreHowItWorksData(t);

  const renderMore = () => (
    <SubContent>
      {data.map((step) => (
        <BlockContainer key={step.slug}>
          <Text bold uppercase>{t('En savoir plus')}</Text>
          <Text>{toFormattedBoldText(step.part1)}</Text>
          {step.part2 && <Text>{toFormattedBoldText(step.part2)}</Text>}
          {step.part3 && <Text>{toFormattedBoldText(step.part3)}</Text>}
        </BlockContainer>
      ))}
    </SubContent>
  );

  const renderMoreMobile = (number: number) => (
    <BlockContainer>
      <Text bold uppercase>{t('En savoir plus')}</Text>
      <Text>{toFormattedBoldText(data[number - 1].part1)}</Text>
      {data[number - 1].part2 && <Text>{toFormattedBoldText(data[number - 1].part2)}</Text>}
      {data[number - 1].part3 && <Text>{toFormattedBoldText(data[number - 1].part3)}</Text>}
    </BlockContainer>
  );

  const stepsData = () => {
    if (landingPageType === 'atHome') {
      return stepsAtHome(t, position || '');
    } if (landingPageType === 'package') {
      return stepsPackage(t);
    }
    return introHowItWorksData(t);
  };

  return (
    <>
      <SubContent>
        {stepsData().map((intro) => (
          <BlockContainer key={intro.number}>
            <SubtitleContainer>
              <NumberContainer>
                <Number>{intro.number}</Number>
                <Dot />
              </NumberContainer>
              <Subtitle>
                {slashNtoBr(intro.subtitle)}
              </Subtitle>
            </SubtitleContainer>
            <Text>{slashNtoBr(intro.text)}</Text>
            {((isZipcodeComplete || isPackage) && isMobile) && renderMoreMobile(intro.number)}
          </BlockContainer>
        ))}
      </SubContent>
      {((isZipcodeComplete || isPackage) && !isMobile) && renderMore()}
    </>

  );
};

MainHowItWorks.defaultProps = {
  position: '',
};

export default withAppContext(MainHowItWorks);
